/**
 * THE DISPLAYED SCIENTIFIC NAME (operator ruling, 2026-10-10).
 *
 *   formatScientificName('Dendrobates', 'Tinctorius', 'Azureus', 0)
 *     -> 'Dendrobates tinctorius "Azureus"'
 *   formatScientificName('Philodendron', 'Pink Princess', null, 1)
 *     -> 'Philodendron Pink Princess'   (a PLANT is left as typed)
 *
 * Genus capitalised; the species epithet and any subspecies word
 * LOWERCASE; a trade name inside quotes or parentheses exactly as typed;
 * the LOCALITY — when one is passed — in straight double quotes, the
 * hobby's own convention. Most prod species store the epithet
 * capitalised ("Tinctorius"), and the ruling fixes the DISPLAY here, in
 * code, never the data: every one of those species already has a URL
 * Google knows.
 *
 * ⚠️ ONE IMPLEMENTATION ON THE CLIENT, a declared PORT of
 * `facility/services/species_names.display_scientific_name` and mirrored
 * case for case by `__tests__/scientificName.test.js`.
 *
 * ⚠️ DISPLAY ONLY. Never pass its output to a slug, a route, a lookup or
 * a filter — `slugUtils` reads the raw fields and its output is pinned
 * byte-for-byte by `__tests__/speciesUrlIdentity.test.js`.
 */

/** Open quote -> its close; a segment between them is a proper name. */
const QUOTE_PAIRS = {
  '"': '"', "'": "'", '“': '”', '‘': '’', '(': ')',
};
/** An ANIMAL locality already carrying one of these is printed as it is. */
const QUOTE_MARKS = ['"', '“', '”'];
/** …and a PLANT cultivar's: single quotes count too. */
const PLANT_QUOTE_MARKS = [...QUOTE_MARKS, "'", '‘', '’'];

/** `AnimalTypes.PLANT` (facility/enums.py). */
export const SPECIES_TYPE_PLANT = 1;

const isAlnum = (ch) => /[\p{L}\p{N}]/u.test(ch || '');

const str = (v) => (v === null || v === undefined ? '' : String(v)).trim();

/** The species type as a number, or null when it is not known. */
const asType = (t) => {
  if (t === null || t === undefined || t === '' || typeof t === 'boolean') return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : null;
};

/** True only for a KNOWN plant type. */
export const isPlantType = (t) => asType(t) === SPECIES_TYPE_PLANT;

/**
 * True only for a KNOWN type that is not a plant. ⚠️ THE EPITHET IS
 * LOWERCASED ONLY WHEN THIS IS TRUE (driver ruling, 2026-10-10): the
 * operator's request concerned ANIMALS, a botanical cultivar keeps its
 * capitals, and a caller that cannot say what the species is gets the
 * name AS TYPED — never lowercased on a guess.
 */
export const isKnownAnimalType = (t) => {
  const n = asType(t);
  return n !== null && n !== SPECIES_TYPE_PLANT;
};

/**
 * A known ANIMAL: every BARE word lowercase, quoted / parenthesised
 * segments verbatim. A plant, or a type not known: as typed.
 */
export const formatEpithet = (epithet, speciesType) => {
  const text = str(epithet);
  if (!isKnownAnimalType(speciesType)) return text;
  let out = '';
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    const close = QUOTE_PAIRS[ch];
    // An apostrophe INSIDE a word is not a quote.
    if (close && !(ch === "'" && i > 0 && isAlnum(text[i - 1]))) {
      const end = text.indexOf(close, i + 1);
      if (end < 0) {
        out += text.slice(i);
        break;
      }
      out += text.slice(i, end + 1);
      i = end + 1;
      continue;
    }
    out += ch.toLowerCase();
    i += 1;
  }
  return out;
};

/** First letter capitalised, the rest as typed ("QADELETE" stays). */
export const formatGenus = (genus) => {
  const text = str(genus);
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * '"Azureus"' — straight double quotes for an animal (or a type not
 * known); a PLANT's cultivar in the botanical single quotes. Never
 * doubled; '' for none.
 */
export const formatLocality = (locality, speciesType) => {
  let text = str(locality);
  if (!text) return '';
  if (isPlantType(speciesType)) {
    if (PLANT_QUOTE_MARKS.some((m) => text.includes(m))) return text;
    return `'${text}'`;
  }
  if (QUOTE_MARKS.some((m) => text.includes(m))) return text;
  // A locality STORED in a matched pair of single quotes — straight or
  // curly (run 42, the server's twin) — is the word inside them.
  const last = text[text.length - 1];
  if (text.length >= 2
      && ((text[0] === "'" && last === "'") || (text[0] === '\u2018' && last === '\u2019'))) {
    text = text.slice(1, -1).trim();
  }
  return `"${text}"`;
};

/**
 * 'Dendrobates tinctorius "Azureus"' — THE displayed species name.
 * `speciesType` decides the epithet: only a KNOWN animal type is
 * lowercased; omitted means not known, and the name is shown as typed.
 */
export const formatScientificName = (genus, species, locality, speciesType) => {
  const name = [formatGenus(genus), formatEpithet(species, speciesType)]
    .filter(Boolean).join(' ');
  const loc = formatLocality(locality, speciesType);
  return loc ? `${name} ${loc}`.trim() : name;
};

/**
 * `formatScientificName` for an object with genus / species / locality /
 * species_type (the type read off the object itself).
 */
export const formatSpeciesName = (obj, { withLocality = true } = {}) => {
  if (!obj) return '';
  return formatScientificName(obj.genus, obj.species,
    withLocality ? obj.locality : undefined, obj.species_type);
};

export default formatScientificName;
